import React, { useEffect, useState } from 'react';
import Avatar from '../common/Avatar';
import ThreeDots from '../Icons/ThreeDots';
import Dropdown from '../common/Dropdown';
import ConfirmationModal from '../Modal/ConfirmationModal';
import timeSpan from '../../utils/timeSpan';
import { useDispatch, useSelector } from 'react-redux';
import { deleteCommentDispatcher } from '../../redux/dispatchers/feedDispatcher';
import { getErrorMessage, successStatus } from '../../common';
import { ToastNotifyError } from '../Toast/ToastNotify';
import CommentInput from './CommentInput';
import HtmlText from '../common/HtmlText';
import { DATE_FORMAT } from '../../constants/constants';
import ReportComment from './ReportComment';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../constants/urlPaths';
import LikeFilledIcon from '../Icons/LikeFilledIcon';
import LikeEmptyIcon from '../Icons/LikeEmptyIcon';
import { likeComment } from '../../services/feed';

const CommentLayout = ({
  PostId,
  User,
  id,
  createdAt,
  description,
  isLikedByMe = false,
  likeCount,
  reloadPostDetails = () => {},
}) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.auth?.user) || {};
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const { globalTransparentLoadingPrivate } = useSelector((state) => state.auth || {});
  const myProfile = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [likeByMe, setLikeByMe] = useState(isLikedByMe);
  const [isAnimating, setIsAnimating] = useState(false);
  const [likeCountState, setLikeCountState] = useState(likeCount);

  const deleteCommentHandler = async () => {
    if (!globalTransparentLoadingPrivate) {
      const { status, data } = (await dispatch(deleteCommentDispatcher({ id }))) || {};
      if (!successStatus(status)) {
        const errormsg = getErrorMessage(data);
        if (errormsg) {
          ToastNotifyError(errormsg);
        }
      }
      await reloadPostDetails(PostId);
    }
  };
  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => setIsAnimating(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  const handleLike = async () => {
    // if (!globalTransparentLoadingPrivate) {
    // const { status, data } = (await dispatch(likeCommentDispatcher({ id }))) || {};
    setLikeCountState(likeByMe ? likeCountState - 1 : likeCountState + 1);
    setLikeByMe(!likeByMe);
    setIsAnimating(true); // Trigger the animation

    likeComment({ id });
    // await reloadPostDetails(PostId);
    // }
  };

  return (
    <div className="flex gap-2 mt-3 w-full">
      {!isEditing ? (
        <>
          <Avatar
            image={User?.profilePictureUrl}
            name={`${User?.firstName} ${User?.lastName}`}
            classNames="w-[40px] h-[40px] cursor-pointer"
            clickFun={() => {
              if (User.id !== myProfile.id) {
                navigate(`${PATHS.OTHER_USER_PROFILE}${User?.id}`);
              } else {
                navigate(`${PATHS.PROFILE}`);
              }
            }}
          />
          <div className="relative flex flex-col justify-center gap-1 p-2 bg-whitelight rounded-lg w-full">
            <div className="flex justify-between items-center">
              <div
                className="text14 font-semibold text-greydark max-w-[55%] text-ellipsis overflow-hidden cursor-pointer"
                title={`${User?.firstName} ${User?.lastName}`}
                onClick={() => {
                  if (User.id !== myProfile.id) {
                    navigate(`${PATHS.OTHER_USER_PROFILE}${User?.id}`);
                  } else {
                    navigate(`${PATHS.PROFILE}`);
                  }
                }}
              >
                {User?.firstName} {User?.lastName}
              </div>
              <div className="flex items-center justify-center gap-3 pr-2">
                <div className="cursor-pointer flex" onClick={() => handleLike()}>
                  <div
                    className={`cursor-pointer w-[16px] h-[16px] flex items-center justify-content-center ${isAnimating ? 'scale-bounce' : ''}`}
                  >
                    {likeByMe ? <LikeFilledIcon /> : <LikeEmptyIcon />}
                  </div>
                  {likeCountState > 0 && (
                    <span className="text-xs text-greylight ml-1">{likeCountState}</span>
                  )}
                </div>
                <p className="text-[11px] lg:text-xs text-greylight">
                  {timeSpan(createdAt, DATE_FORMAT.CAPTION)}
                </p>
                <div className="w-[20px] relative top-1 flex justify-center items-center">
                  <Dropdown
                    options={
                      User?.id === userData?.id
                        ? [
                            { name: 'Edit', action: () => setIsEditing(true) },
                            { name: 'Delete', action: () => setIsDeleteModalOpen(true) },
                          ]
                        : [{ name: 'Report', action: () => setIsReportModalOpen(true) }]
                    }
                    IconComponent={() => <ThreeDots className="w-[4px] h-[20px] rotate-90" />}
                    optionsClassName="!top-0"
                  />
                </div>
              </div>
            </div>

            <ConfirmationModal
              title="Delete Comment"
              isOpen={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              primaryButtonTitle="Delete"
              secondaryButtonTitle="No"
              secondaryButtonAction={() => setIsDeleteModalOpen(false)}
              primaryButtonAction={deleteCommentHandler}
            >
              Are you sure you want to delete this comment?
            </ConfirmationModal>

            <ReportComment
              isOpen={isReportModalOpen}
              onClose={() => setIsReportModalOpen(false)}
              commentId={id}
              postId={PostId}
              commentorId={User?.id}
            />

            <div className="text-[13px] lg:text14 text-greydark break-all">
              <HtmlText
                text={
                  showFullDescription
                    ? description
                    : `${description.slice(0, 300)}${description.length > 300 ? '...' : ''}`
                }
              />
              {description.length > 300 && (
                <button
                  className="text-[13px] lg:text14 text-blue-500 cursor-pointer font-semibold"
                  onClick={toggleDescription}
                >
                  {showFullDescription ? 'Less' : 'More'}
                </button>
              )}
            </div>
          </div>
        </>
      ) : (
        <CommentInput
          reloadPostDetails={reloadPostDetails}
          postId={PostId}
          isEditing={true}
          cancelEditing={() => setIsEditing(false)}
          commentDetails={{
            description,
            id,
            userId: User?.id,
          }}
        />
      )}
    </div>
  );
};

export default CommentLayout;
